import { Avatar, Form, Pagination, Row, message } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { ColumnsType } from 'antd/es/table';
import { TableRowSelection } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Box from '../../components/Box';
import Text from '../../components/Text';
import accountApis from '../../services/accountApis';
import { setLoading } from '../../store/loading/actions';
import { setShopState } from '../../store/shop/actions';
import { formatPhone } from '../../utils/formatPhone';
import { PageStyled } from '../styles';
import { TableEvenOdd } from './styles';
import { IcEdit, IcReturn, IcTrash } from 'components/Icon/icons';
import UpdateStaffModal, {
  UpdateStaffModalRef,
} from './components/UpdateStaffModal';
import ModalConfirm, {
  ModalConfirmRef,
} from 'pages/employees/components/ModalConfirm';

export enum StaffStatus {
  ACTIVE = 'ACTIVE',
  DELETE = 'DELETE',
}
export interface IStaffItemData {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  staffType: string;
  createdDate: string;
  birthday: string | null;
  staffSalaryType: string | null;
  status: string;
  urlImage: string;
  isSelected?: boolean;
  isBusy?: boolean;
  value?: string;
  label?: string;
  anyBody: boolean;
  address: string;
  city: string;
  state: string;
  zip: string;
  password: string;
}

export const SalonEmployeesPage = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [lstGroupByShopId, setLstGroupByShopId] = useState<any>(null);
  const [data, setData] = useState<IStaffItemData[]>([]);
  const [totalItems, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const updateStaffRef = useRef<UpdateStaffModalRef>(null);
  const modalConfirmRef = useRef<ModalConfirmRef>(null);

  const columns: ColumnsType<IStaffItemData> = [
    {
      title: 'First name',
      dataIndex: 'firstName',
      key: 'firstName',
      align: 'left',
      render: (text, record) => (
        <Row align={'middle'} justify={'start'} style={{ gap: '10px' }}>
          <Avatar
            size={'small'}
            src={record.urlImage}
            style={{ backgroundColor: '#f56a00' }}
          >
            {text?.[0] || 'A'}
          </Avatar>
          <Text variant='BODY_1' color='text_3' textAlign='left'>
            {text}
          </Text>
        </Row>
      ),
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      key: 'lastName',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {text}
        </Text>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {text || '--'}
        </Text>
      ),
    },
    {
      title: 'Pin Code',
      dataIndex: 'password',
      key: 'password',
      align: 'center',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='center'>
          {text}
        </Text>
      ),
    },
    {
      title: 'DOB',
      dataIndex: 'birthday',
      key: 'DOB',
      align: 'left',
      render: (text) => {
        const date = dayjs(text);
        return (
          <Text variant='BODY_1' color='text_3' textAlign='left'>
            {date.isValid() ? date.format('MM/DD') : '--'}
          </Text>
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {formatPhone(text)}
        </Text>
      ),
    },
    {
      title: 'Salary',
      dataIndex: 'staffSalaryType',
      key: 'salary',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {text || '--'}
        </Text>
      ),
    },
    {
      title: 'Position',
      dataIndex: 'staffType',
      key: 'position',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {getStaffPositionLabel(text)}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      render: (text) => (
        <StatusTag className={text}>
          <Text variant='BODY_1' color='text_1' textAlign='left'>
            {text === 'ACTIVE' ? 'ACTIVE' : 'DELETE'}
          </Text>
        </StatusTag>
      ),
    },
    {
      title: 'Create Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      align: 'left',
      render: (text) => (
        <Text variant='BODY_1' color='text_3' textAlign='left'>
          {dayjs(text).format('MM-DD-YYYY')}
        </Text>
      ),
    },
    {
      title: <span>Actions</span>,
      width: '100px',
      dataIndex: 'id',
      render: (id: string, record: IStaffItemData) => (
        <ActionButtonsStyled>
          <ActionButtonStyled
            className='btn-edit'
            onClick={() => handleUpdateStaff(record)}
          >
            <IcEdit fill='#ffffff' />
          </ActionButtonStyled>
          {record.status === StaffStatus.ACTIVE && (
            <ActionButtonStyled
              className='btn-delete'
              onClick={() => handleOpenModalConfirmDelete(record)}
            >
              <IcTrash fill='#ffffff' />
            </ActionButtonStyled>
          )}
          {record.status === StaffStatus.DELETE && (
            <ActionButtonStyled
              className='btn-restore'
              onClick={() => handleOpenModalConfirmRestore(record)}
            >
              <IcReturn fill='#ffffff' />
            </ActionButtonStyled>
          )}
        </ActionButtonsStyled>
      ),
    },
  ];

  const handleUpdateStaff = (staff: IStaffItemData) => {
    updateStaffRef.current?.open(staff, async () => {
      await getDataCustomer(id, currentPage);
    });
  };

  const getDataCustomer = async (shopId: any, page: number) => {
    try {
      const response = await accountApis.getAllStaff(shopId, page);
      if (response.status === 200) {
        setData(response.data.data.content);
        setTotal(response.data.data.totalElements);
      } else {
        message.error({ content: response.data.message, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: error.message as string, duration: 2 });
    }
  };

  const onDeleteStaff = async (staffId: string) => {
    try {
      dispatch(setLoading(true));
      const response = await accountApis.updateStaffStatus({
        staffId: staffId,
        status: StaffStatus.DELETE,
      });

      if (response.status === 200) {
        message.success('Staff deleted successfully');
        modalConfirmRef.current?.close();
        await getDataCustomer(id, currentPage);
      } else {
        message.error({ content: response.data.message, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: error.message as string, duration: 2 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onRestoreStaff = async (staffId: string) => {
    try {
      dispatch(setLoading(true));
      const response = await accountApis.updateStaffStatus({
        staffId: staffId,
        status: StaffStatus.ACTIVE,
      });

      if (response.status === 200) {
        message.success('Staff restored successfully');
        modalConfirmRef.current?.close();
        await getDataCustomer(id, currentPage);
      } else {
        message.error({ content: response.data.message, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: error.message as string, duration: 2 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOpenModalConfirmDelete = (staff: IStaffItemData) => {
    modalConfirmRef.current?.open(
      `Are you sure you want to delete ${staff.firstName} ${staff.lastName} ?`,
      `This action cannot be undone.`,
      async () => {
        await onDeleteStaff(staff.id);
      }
    );
  };

  const handleOpenModalConfirmRestore = (staff: IStaffItemData) => {
    modalConfirmRef.current?.open(
      `Are you sure you want to restore ${staff.firstName} ${staff.lastName} ?`,
      `This action cannot be undone.`,
      async () => {
        await onRestoreStaff(staff.id);
      }
    );
  };

  useEffect(() => {
    const initData = async () => {
      try {
        if (id && !lstGroupByShopId) {
          dispatch(setLoading(true));
          const response = await accountApis.allCustomerGroupListByShopId(id);
          if (response.status === 200) {
            setLstGroupByShopId(response.data.data);
          }
          await getDataCustomer(id, currentPage);

          const resShopById = await accountApis.getShopById(id);
          if (resShopById.status === 200) {
            dispatch(setShopState(resShopById.data.data));
          }
          dispatch(setLoading(false));
        }
      } catch (error: any) {
        message.error({ content: error.message as string, duration: 2 });
      }
    };
    initData();
  }, []);

  const onPageChanged = async (pageNumber: number) => {
    dispatch(setLoading(true));
    try {
      setCurrentPage(pageNumber);
      const values = await formSearch.validateFields();
      await getDataCustomer(id, pageNumber);
    } catch (error) {
      message.error({ content: 'Page has been deleted', duration: 2 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <PageStyled>
      <ContentStyled>
        <Box className='d-between' mb={2}>
          <Text variant='H5'>Employee List ({totalItems})</Text>
        </Box>
        <TableContentStyled
          // @ts-ignore
          columns={columns}
          dataSource={data}
          rowKey='id'
          pagination={false}
        />

        <PagingStyled>
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={20}
            onChange={onPageChanged}
            showSizeChanger={false}
          />
        </PagingStyled>
      </ContentStyled>
      <ModalConfirm ref={modalConfirmRef} />
      <UpdateStaffModal ref={updateStaffRef} />
    </PageStyled>
  );
};

const ContentStyled = styled.div`
  background: rgba(255, 255, 255, 1);
  margin-top: 2rem;
  padding: 1rem;
`;

const TableContentStyled = styled(TableEvenOdd)`
  && .shopName {
    width: 15%;
  }

  && .endTime {
    width: 15%;
  }
`;

const PagingStyled = styled(Content)`
  text-align: right;
  margin-top: 10px;
  background: rgba(255, 255, 255, 1);
`;

export enum STAFF_POSITION_INFO {
  ADMIN = 'ADMIN',
  TECHNICAL = 'TECHNICAL',
  CASHIER = 'CASHIER',
  MANAGER = 'MANAGER',
}

const getStaffPositionLabel = (val: STAFF_POSITION_INFO) => {
  switch (val) {
    case STAFF_POSITION_INFO.ADMIN:
      return 'ADMIN';
    case STAFF_POSITION_INFO.CASHIER:
      return 'CASHIER';
    case STAFF_POSITION_INFO.MANAGER:
      return 'MANAGER';
    case STAFF_POSITION_INFO.TECHNICAL:
      return 'TECHNICIAN';
    default:
      return '';
  }
};

const ActionButtonsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButtonStyled = styled.button`
  cursor: pointer;
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  aspect-ratio: 1/1;
  border-radius: 4px;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  &.btn-edit {
    background: #008bff;
  }
  &.btn-delete {
    background: #f53f3f;
  }
  &.btn-restore {
    background: #0da472;
  }
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 40px;
  width: fit-content;
  &.ACTIVE {
    background: #0da472;
  }

  &.DELETE {
    background: #f53f3f;
  }
`;